import React, { Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useFormikContext } from "formik";
import HoverIconButton from "components/HoverIconButton";
import DebounceTextarea from "components/DebounceTextarea";
import PrimaryPriceIntervalPicker from "../PrimaryPriceIntervalPicker";
import FieldGroup from "components/FormerEditor/FieldGroup";
import {
  PRICING_TABLE_INTENT,
  PRODUCT_ORDER_INTENT
} from "utils/constants/pricing";
import StripeExternalLink from "components/StripeExternalLink";
import { STATE_KEYS } from "utils/constants/state";
import { getSchemaForField, getSchemaDependenciesForField } from "utils/schema";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import { schemaPropertiesToValues } from "components/FormerEditor/common";
import { getStripeDestination } from "utils/stripe";
import { withRouter } from "next/router";
import {
  updateMerchantAccountProducts,
  archiveMerchantAccountProducts
} from "actions/product";
import {
  getCtxAppResourceAndIDs,
  getCtxMerchantAccountUUID
} from "utils/mapStateToProps";
import FieldController from "components/FieldController";
import ConfirmIconButton from "components/ConfirmIconButton";
import { getProductArchiveModalSlides } from "utils/constants/modal";
import { resolvePrimariesLayoutOnProductDelete } from "utils/product";
import { string as yupString, object as yupObject } from "yup";
import Button from "components/Form/fields/Button";
import Separator from "components/Separator";
import { setEditorFocus } from "utils/builder";
import { secondaryBackButton } from "utils/constants/ui";
import { usePageBuilderContext } from "utils/context";
import Analytics, { DELETE_PRODUCT, ADD_PRICE } from "utils/analytics";
import Padder from "components/Padder";
import CopyCode from "components/CopyCode";
import { APPLICATION } from "utils/constants/models";
import { getModelIDFromQuery } from "utils/route";

const COMPONENT = "ProductForm";

const ProductForm = ({
  name,
  product,
  productIndex,
  variableContext,
  schema,
  resource,
  resourceUUID,
  merchantAccountUUID,
  actions: { updateMerchantAccountProducts, archiveMerchantAccountProducts }
}) => {
  const { tracking } = usePageBuilderContext();
  const {
    values,
    setValues,
    setTouched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const schemaKey = "products";
  const productPath = `${name}.data.${schemaKey}[${productIndex}]`;
  const schemaProps = getSchemaForField(schema, `properties.${schemaKey}`);
  const account = get(values, `${STATE_KEYS.MANIFEST.ADMIN}.account`, {});
  const descriptionPath = `${productPath}.description`;
  const description = get(values, descriptionPath, "");

  const syncProduct = (updatedProduct, callback) => {
    const data = {
      products: [
        {
          id: product.uid,
          ...updatedProduct
        }
      ]
    };
    updateMerchantAccountProducts(
      {
        resourceUUID,
        resource,
        uuid: merchantAccountUUID,
        data
      },
      callback
    );
  };

  const buttonProps = secondaryBackButton({
    onClick: setEditorFocus({
      values,
      setValues,
      focus: [
        {
          key: STATE_KEYS.EDITOR.PRICING_ACTIVE_PRODUCT,
          value: null
        },
        {
          key: STATE_KEYS.EDITOR.PRICING_ACTIVE_PRICE,
          value: null
        },
        {
          key: STATE_KEYS.EDITOR.PRICING_INTENT,
          value: PRICING_TABLE_INTENT.PRODUCT_ORDER
        }
      ]
    }),
    tooltip: "Back"
  });

  const FIELD_GROUPS = [
    <FieldController
      name="title"
      label="Name"
      key={`${productPath}.name`}
      rootPath={productPath}
      fieldKey="name"
      onSet={syncProduct}
      validationSchema={yupObject().shape({
        title: yupString().required("Product must have a name")
      })}
    />,
    <Fragment>
      <label className="f6 mb2 dib fw6">{"Description"}</label>
      <DebounceTextarea
        inputValue={description}
        placeholder="Describe this product"
        debounce={500}
        minRows={3}
        name={descriptionPath}
        valueSet={(val) => {
          setFieldValue(descriptionPath, val);
          setFieldTouched(descriptionPath, true);
        }}
      />
    </Fragment>,
    <Separator customClasses={{ container: "w-100 pv3 dib" }} />,
    <Fragment>
      <div className="flex flex-row justify-between items-center">
        <div className="fw5 f5 ttu tracked">{`Prices`}</div>
        <Button
          theme="slim"
          onClick={() => {
            Analytics.track(ADD_PRICE, {
              ...tracking,
              component: COMPONENT,
              copy: "Create price"
            });
            setFieldValue(
              STATE_KEYS.EDITOR.PRODUCT_ORDER_INTENT,
              PRODUCT_ORDER_INTENT.PRICE_ADD
            );
          }}
          copy="Create price"
        />
      </div>
      <PrimaryPriceIntervalPicker name={`${productPath}`} />
    </Fragment>
  ];

  const subtextSchema = getSchemaForField(schemaProps, "subtext");
  const subtextTemplate = schemaPropertiesToValues(subtextSchema);

  if (!isEmpty(subtextTemplate)) {
    FIELD_GROUPS.push(
      <Fragment>
        <FieldGroup
          label="Subtext"
          key={`${productPath}.subtext`}
          variableContext={variableContext}
          name={`${productPath}.subtext`}
          collection={product.subtext}
          schema={subtextSchema}
          template={subtextTemplate}
        />
      </Fragment>
    );
  }

  const actionsSchema = getSchemaForField(schemaProps, "actions");
  const actionsDependencies = getSchemaDependenciesForField(
    schemaProps,
    "actions"
  );
  const actionsTemplate = schemaPropertiesToValues(actionsSchema);

  if (!isEmpty(actionsTemplate)) {
    FIELD_GROUPS.push(
      <Fragment>
        <Separator
          customClasses={{ container: "w-100 pv3 dib entities__actions" }}
        />
        <FieldGroup
          label="Actions"
          variableContext={variableContext}
          key={`${productPath}.actions`}
          name={`${productPath}.actions`}
          collection={product.actions}
          schema={getSchemaForField(schemaProps, "actions")}
          dependencies={actionsDependencies}
          template={actionsTemplate}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="flex flex-column items-start justify-between pl1 pr3 pv3 sticky bb hairline-1 bg-white top-0 z-1 bh--inset">
        <div className="flex flex-row items-center">
          <HoverIconButton {...buttonProps} />
          <div className="fw5 f4 pl2">{product.title}</div>
        </div>
      </div>
      <div className="bg-content--secondary pl3 pv2 bb hairline-1 pr2 bh--inset">
        <ConfirmIconButton
          slides={getProductArchiveModalSlides(product)}
          icon="archive"
          copy="Archive product"
          onConfirm={(callback) => {
            Analytics.track(DELETE_PRODUCT, {
              ...tracking,
              component: COMPONENT,
              copy: "Delete product"
            });
            const deleteDataInput = {
              products: [
                {
                  id: product.uid
                }
              ]
            };
            archiveMerchantAccountProducts(
              {
                resourceUUID,
                resource,
                uuid: merchantAccountUUID,
                data: deleteDataInput
              },
              (err, products) => {
                if (products && products.length) {
                  /**
                   * Remove product from primaries and layout
                   */
                  resolvePrimariesLayoutOnProductDelete({
                    values,
                    products,
                    setValues,
                    setTouched
                  });
                  /**
                   * Close the product menu
                   */
                  setFieldValue(STATE_KEYS.EDITOR.PRICING_ACTIVE_PRODUCT, null);
                }
                callback(err, products);
              }
            );
          }}
        >
          <div className="fl pv3 relative z-1">
            <StripeExternalLink
              account={account}
              copy={`Manage on Stripe`}
              href={getStripeDestination({ path: `products/${product.uid}` })}
            />
          </div>
        </ConfirmIconButton>
      </div>
      <CopyCode
        customClasses={{
          content: "flex flex-row items-center ph3 pt3"
        }}
        value={product.uid}
        tooltip="Copy product id"
        toast={"Copied product id to clipboard"}
      />
      <div className="ph3 bg-white bh--inset">
        {FIELD_GROUPS.map((fieldGroup, fieldGroupIx) => (
          <div key={fieldGroupIx} className="pt3">
            {fieldGroup}
          </div>
        ))}
      </div>
      <Padder theme="visible" />
    </Fragment>
  );
};

ProductForm.propTypes = {
  name: PropTypes.string.isRequired,
  product: PropTypes.object.isRequired,
  productIndex: PropTypes.number.isRequired,
  variableContext: PropTypes.array.isRequired,
  schema: PropTypes.object.isRequired
};

const mapStateToProps = (state, { router }) => {
  const applicationUUID = getModelIDFromQuery(APPLICATION, router.query);

  return {
    ...getCtxAppResourceAndIDs(state, applicationUUID),
    merchantAccountUUID: getCtxMerchantAccountUUID(state)
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateMerchantAccountProducts,
      archiveMerchantAccountProducts
    },
    dispatch
  )
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProductForm)
);
